import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { notify } from 'shared/lib/notification';
import {
  getRouteOtpCheck,
  getRouteRussiaExistingAccount,
} from 'shared/consts/routes';
import { CreateInviteValues } from '../../api/createInvite';
import { validationScheme } from '../../lib/validationScheme';
import { Button, Checkbox, Flex, MaterialInput, Typography } from 'shared/_ui';

import styles from './styles.module.scss';
import { useSendOtp, useStartRuOnboardingOtp } from 'shared/api';
import i18next from 'i18next';
import { clearStoredUtmTags, getStoredUtmTags } from 'shared/lib/utmTags';
import { sendAnalyticsEvent } from 'shared/lib/analytics';
import { clearAuthFlow, setAuthFlow } from 'shared/lib/authFlow';
import { clearIngressPath } from 'shared/lib/ingressPath';

type CreateInviteProps = {
  ruOnboarding?: boolean;
};

export const CreateInvite: React.FC<CreateInviteProps> = ({
  ruOnboarding = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [otplogin, { isLoading }] = useSendOtp();
  const [startRuOnboarding, { isLoading: isRuOnboardingLoading }] =
    useStartRuOnboardingOtp();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateInviteValues>({
    mode: 'onSubmit',
    defaultValues: { email: '' },
    resolver: yupResolver(validationScheme),
  });
  const [isAgree, setIsAgree] = React.useState<boolean>(true);

  const onSubmit = async (data: CreateInviteValues) => {
    if (!isAgree) {
      notify.error(t('authentication.notify.onDisagreeance'));
      return;
    }
    localStorage.setItem('user-email', data.email);
    setAuthFlow('sign_up');
    const request = {
      email: data.email,
      languageCode: i18next.language,
      ...getStoredUtmTags(),
    };
    try {
      if (ruOnboarding) {
        const response = await startRuOnboarding(request).unwrap();
        if (response.status === 'ALREADY_REGISTERED') {
          localStorage.removeItem('user-email');
          clearAuthFlow();
          clearIngressPath();
          clearStoredUtmTags();
          navigate(getRouteRussiaExistingAccount(), { replace: true });
          return;
        }
      } else {
        await otplogin(request).unwrap();
      }

      sendAnalyticsEvent('trial_start', { plan: 'trial' });
      navigate(getRouteOtpCheck(pathname));
    } catch (error: unknown) {
      clearAuthFlow();
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status === 400
      ) {
        notify.error(t('authentication.notify.invitationAlreadySent'));
      } else {
        notify.error(t('shared.error.base'));
      }
    }
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" spacing={16} className={styles.wrapper}>
        <div className={styles.field}>
          <MaterialInput
            type="email"
            placeholder={t('authentication.registration.email')}
            {...register('email')}
          />
          {errors.email && (
            <Typography.Text type="danger">
              {t(
                errors.email?.message
                  ? errors.email?.message
                  : 'shared.error.base'
              )}
            </Typography.Text>
          )}
        </div>
        <Checkbox
          className={styles.checkbox}
          checked={isAgree}
          onChange={(e) => {
            setIsAgree(e.target.checked);
          }}
        >
          <div className={styles.agreement}>
            <Typography.Text type="secondary">
              {t('authentication.registration.agreement')}
              &nbsp;
              <Typography.Link href={t('links.offer')} target="_blank">
                {t('authentication.registration.offerAgreement')}
              </Typography.Link>
              &nbsp;
              {t('authentication.registration.and')}
              &nbsp;
              <Typography.Link href={t('links.privacyPolicy')} target="_blank">
                {t('authentication.registration.privacyPolicy')}
              </Typography.Link>
            </Typography.Text>
          </div>
        </Checkbox>
        <Button
          loading={isLoading || isRuOnboardingLoading}
          className={styles.button}
          type="primary"
          analyticsEventName="click_START"
        >
          {t('authentication.registration.next')}
        </Button>
      </Flex>
    </form>
  );
};
